import { useTheme } from "@emotion/react";
import { Typography } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Avatar } from "@aviary";
import FullscriptLogoGreenLeafPigeonTextSVGComponent from "@shared/assets/logo/FullscriptLogoGreenLeafPigeonText";
import FullscriptLogoWhiteTextGreenLeafSVGComponent from "@shared/assets/logo/FullscriptLogoWhiteTextGreenLeaf";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { PatientInfoBlockSkeleton } from "./PatientInfoBlockSkeleton";

import * as styles from "./PatientInfoBlock.styles";

interface Props {
  storeName: string;
  pracAvatar?: string;
  customMarketing?: string | null;
  loading?: boolean;
  children: ReactNode;
}

const PatientInfoBlock = ({ children, storeName, pracAvatar, customMarketing, loading }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const fsLogo = () => {
    if (theme.theme === "dark") {
      return (
        <FullscriptLogoWhiteTextGreenLeafSVGComponent
          css={styles.svgContainer}
          data-testid="fullscript-logo-dark"
        />
      );
    }

    return (
      <FullscriptLogoGreenLeafPigeonTextSVGComponent
        css={styles.svgContainer}
        data-testid="fullscript-logo"
      />
    );
  };

  if (loading)
    return (
      <Arrange isVertical isFullWidth justify="center" alignment="center" css={styles.fullHeight}>
        {fsLogo()}
        <PatientInfoBlockSkeleton />
      </Arrange>
    );

  const renderCarousel = () => (
    <>
      <Typography variant="h4">
        {t(l.patientRxAuth.practiceLandingPages.leftColumn.tips, {
          storeName,
          interpolation: { escapeValue: false },
        })}
      </Typography>

      {children}
    </>
  );

  return (
    <Arrange isVertical justify="center" alignment="center" css={styles.fullHeight}>
      {fsLogo()}
      {!!pracAvatar && (
        <Avatar
          image={pracAvatar}
          label={t(l.patientRxAuth.practiceLandingPages.leftColumn.pracAvatar, {
            storeName,
            interpolation: { escapeValue: false },
          })}
          size="large"
        />
      )}

      {customMarketing ? <Typography variant="h4">{customMarketing}</Typography> : renderCarousel()}
    </Arrange>
  );
};

export { PatientInfoBlock };
